/**
 * Lakeview Test Data
 *
 * Consolidated Lakeview test data for development utilities.
 * This data is used for quick-fill functionality in dev mode only.
 */

import type { ExistingDebtPaymentInput } from '~/types';

/**
 * Quick-fill test data for Lakeview project (development only)
 */
export const LAKEVIEW_QUICK_FILL_DATA = {
  dates: {
    startingTaxYear: '2025',
    fiscalYearEnd: '2026-06-30',
    taxCollectionSchedule: 'summer' as const,
    paymentDates: [
      { date: '2025-11-01', revenuePercentage: '70.00', isInterestOnly: true },
      { date: '2026-05-01', revenuePercentage: '30.00', isInterestOnly: false },
    ],
  },
  tax: {
    initialTaxableValue: '730473932',
    startYearGrowthRate: '30.00',
    fiveYearGrowthRate: '3.00',
    twentyYearGrowthRate: '3.00',
    startingTaxYear: '2025',
  },
  millage: {
    peakMillage: '7.50',
    peakMillageDuration: '3',
    minimumMillage: '0.40',
    minimumMillageDuration: '1',
  },
  drf: {
    startingBalance: '1713282',
    minimumBalance: '250000',
    maximumBalance: '2500000',
  },
} as const;

/**
 * Lakeview 2016 existing debt payment schedule
 */
export const lakeview2016: ExistingDebtPaymentInput[] = [
  { paymentDate: '2025-11-01', interestRate: 0.0, principalPayment: 0, principalInterest: 0 },
  {
    paymentDate: '2026-05-01',
    interestRate: 0.05,
    principalPayment: 1610000,
    principalInterest: 0,
  },
  { paymentDate: '2026-11-01', interestRate: 0.0, principalPayment: 0, principalInterest: 0 },
  {
    paymentDate: '2027-05-01',
    interestRate: 0.05,
    principalPayment: 1610000,
    principalInterest: 0,
  },
];

/**
 * Lakeview 2020 existing debt payment schedule
 */
export const lakeview2020: ExistingDebtPaymentInput[] = [
  { paymentDate: '2025-11-01', interestRate: 0.05, principalPayment: 100000, principalInterest: 0 },
  { paymentDate: '2026-05-01', interestRate: 0.0, principalPayment: 0, principalInterest: 0 },
  { paymentDate: '2026-11-01', interestRate: 0.05, principalPayment: 100000, principalInterest: 0 },
  { paymentDate: '2027-05-01', interestRate: 0.0, principalPayment: 0, principalInterest: 0 },
  {
    paymentDate: '2027-11-01',
    interestRate: 0.05,
    principalPayment: 1000000,
    principalInterest: 0,
  },
  { paymentDate: '2028-05-01', interestRate: 0.0, principalPayment: 0, principalInterest: 0 },
  {
    paymentDate: '2028-11-01',
    interestRate: 0.05,
    principalPayment: 1000000,
    principalInterest: 0,
  },
  { paymentDate: '2029-05-01', interestRate: 0.0, principalPayment: 0, principalInterest: 0 },
  {
    paymentDate: '2029-11-01',
    interestRate: 0.05,
    principalPayment: 1000000,
    principalInterest: 0,
  },
  { paymentDate: '2030-05-01', interestRate: 0.0, principalPayment: 0, principalInterest: 0 },
  {
    paymentDate: '2030-11-01',
    interestRate: 0.05,
    principalPayment: 1000000,
    principalInterest: 0,
  },
  { paymentDate: '2031-05-01', interestRate: 0.0, principalPayment: 0, principalInterest: 0 },
  {
    paymentDate: '2031-11-01',
    interestRate: 0.05,
    principalPayment: 1000000,
    principalInterest: 0,
  },
  { paymentDate: '2032-05-01', interestRate: 0.0, principalPayment: 0, principalInterest: 0 },
  { paymentDate: '2032-11-01', interestRate: 0.04, principalPayment: 965000, principalInterest: 0 },
  { paymentDate: '2033-05-01', interestRate: 0.0, principalPayment: 0, principalInterest: 0 },
  { paymentDate: '2033-11-01', interestRate: 0.02, principalPayment: 960000, principalInterest: 0 },
  { paymentDate: '2034-05-01', interestRate: 0.0, principalPayment: 0, principalInterest: 0 },
  { paymentDate: '2034-11-01', interestRate: 0.02, principalPayment: 960000, principalInterest: 0 },
  { paymentDate: '2035-05-01', interestRate: 0.0, principalPayment: 0, principalInterest: 0 },
  {
    paymentDate: '2035-11-01',
    interestRate: 0.02125,
    principalPayment: 960000,
    principalInterest: 0,
  },
  { paymentDate: '2036-05-01', interestRate: 0.0, principalPayment: 0, principalInterest: 0 },
  {
    paymentDate: '2036-11-01',
    interestRate: 0.0225,
    principalPayment: 960000,
    principalInterest: 0,
  },
  { paymentDate: '2037-05-01', interestRate: 0.0, principalPayment: 0, principalInterest: 0 },
  {
    paymentDate: '2037-11-01',
    interestRate: 0.0225,
    principalPayment: 960000,
    principalInterest: 0,
  },
  { paymentDate: '2038-05-01', interestRate: 0.0, principalPayment: 0, principalInterest: 0 },
  {
    paymentDate: '2038-11-01',
    interestRate: 0.02375,
    principalPayment: 100000,
    principalInterest: 0,
  },
  { paymentDate: '2039-05-01', interestRate: 0.0, principalPayment: 0, principalInterest: 0 },
  {
    paymentDate: '2039-11-01',
    interestRate: 0.02375,
    principalPayment: 100000,
    principalInterest: 0,
  },
];

/**
 * Lakeview 2022 existing debt payment schedule
 */
export const lakeview2022: ExistingDebtPaymentInput[] = [
  {
    paymentDate: '2025-11-01',
    interestRate: 0.05,
    principalPayment: 1765000,
    principalInterest: 0,
  },
  { paymentDate: '2026-05-01', interestRate: 0.0, principalPayment: 0, principalInterest: 0 },
  { paymentDate: '2026-11-01', interestRate: 0.05, principalPayment: 730000, principalInterest: 0 },
  { paymentDate: '2027-05-01', interestRate: 0.0, principalPayment: 0, principalInterest: 0 },
  {
    paymentDate: '2027-11-01',
    interestRate: 0.05,
    principalPayment: 1000000,
    principalInterest: 0,
  },
  { paymentDate: '2028-05-01', interestRate: 0.05, principalPayment: 585000, principalInterest: 0 },
  {
    paymentDate: '2028-11-01',
    interestRate: 0.05,
    principalPayment: 1000000,
    principalInterest: 0,
  },
  { paymentDate: '2029-05-01', interestRate: 0.05, principalPayment: 715000, principalInterest: 0 },
  {
    paymentDate: '2029-11-01',
    interestRate: 0.05,
    principalPayment: 1000000,
    principalInterest: 0,
  },
  { paymentDate: '2030-05-01', interestRate: 0.05, principalPayment: 850000, principalInterest: 0 },
  {
    paymentDate: '2030-11-01',
    interestRate: 0.05,
    principalPayment: 1000000,
    principalInterest: 0,
  },
  { paymentDate: '2031-05-01', interestRate: 0.05, principalPayment: 975000, principalInterest: 0 },
  {
    paymentDate: '2031-11-01',
    interestRate: 0.05,
    principalPayment: 1000000,
    principalInterest: 0,
  },
  {
    paymentDate: '2032-05-01',
    interestRate: 0.05,
    principalPayment: 1110000,
    principalInterest: 0,
  },
  {
    paymentDate: '2032-11-01',
    interestRate: 0.05,
    principalPayment: 1000000,
    principalInterest: 0,
  },
  {
    paymentDate: '2033-05-01',
    interestRate: 0.04,
    principalPayment: 1235000,
    principalInterest: 0,
  },
  {
    paymentDate: '2033-11-01',
    interestRate: 0.04,
    principalPayment: 1000000,
    principalInterest: 0,
  },
  {
    paymentDate: '2034-05-01',
    interestRate: 0.03,
    principalPayment: 1320000,
    principalInterest: 0,
  },
  {
    paymentDate: '2034-11-01',
    interestRate: 0.03,
    principalPayment: 1000000,
    principalInterest: 0,
  },
  {
    paymentDate: '2035-05-01',
    interestRate: 0.03,
    principalPayment: 1420000,
    principalInterest: 0,
  },
  {
    paymentDate: '2035-11-01',
    interestRate: 0.03,
    principalPayment: 1000000,
    principalInterest: 0,
  },
  {
    paymentDate: '2036-05-01',
    interestRate: 0.03,
    principalPayment: 1470000,
    principalInterest: 0,
  },
  {
    paymentDate: '2036-11-01',
    interestRate: 0.03,
    principalPayment: 1000000,
    principalInterest: 0,
  },
  {
    paymentDate: '2037-05-01',
    interestRate: 0.03,
    principalPayment: 1570000,
    principalInterest: 0,
  },
  {
    paymentDate: '2037-11-01',
    interestRate: 0.03,
    principalPayment: 1000000,
    principalInterest: 0,
  },
  {
    paymentDate: '2038-05-01',
    interestRate: 0.03,
    principalPayment: 1665000,
    principalInterest: 0,
  },
  {
    paymentDate: '2038-11-01',
    interestRate: 0.03,
    principalPayment: 2000000,
    principalInterest: 0,
  },
  {
    paymentDate: '2039-05-01',
    interestRate: 0.0225,
    principalPayment: 1320000,
    principalInterest: 0,
  },
  {
    paymentDate: '2039-11-01',
    interestRate: 0.0225,
    principalPayment: 2000000,
    principalInterest: 0,
  },
];
